import { useEffect, useState, useCallback, useRef } from 'react';
import { AdminSidebar } from '../../components/Sidebar';
import QuestionModal from '../../components/QuestionModal';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import api from '../../utils/api';

const LEVEL_INFO = {
  easy:   { cls: 'badge-green',  label: 'Leicht' },
  medium: { cls: 'badge-orange', label: 'Mittel' },
  hard:   { cls: 'badge-red',    label: 'Schwer' },
};

export default function AdminQuestions() {
  const { hasFeature } = useAuth();
  const toast = useToast();
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ category: '', level: '', search: '', source: '' });
  const [modal, setModal] = useState(null); // null | 'new' | question object
  const [deleteId, setDeleteId] = useState(null);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ central: 0, own: 0 });
  const [imageUploading, setImageUploading] = useState(null);
  const imageInputRef = useRef();
  const imageTargetId = useRef(null);

  useEffect(() => {
    api.get('/questions/categories').then(r => setCategories(r.data.categories));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ limit: 100 });
    if (filter.category) p.set('category', filter.category);
    if (filter.level) p.set('level', filter.level);
    if (filter.search) p.set('search', filter.search);
    if (filter.source) p.set('source', filter.source);
    api.get(`/questions?${p}`)
      .then(r => { setQuestions(r.data.questions); setTotal(r.data.total); setCounts({ central: r.data.centralTotal || 0, own: r.data.ownTotal || 0 }); })
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleSaved = (q, isEdit) => {
    if (isEdit) {
      setQuestions(prev => prev.map(x => x.id === q.id ? { ...x, ...q } : x));
    } else {
      setQuestions(prev => [q, ...prev]);
      setTotal(t => t + 1);
    }
    setModal(null);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/questions/${id}`);
      setQuestions(prev => prev.filter(q => q.id !== id));
      setTotal(t => t - 1);
    } catch (err) {
      toast(err.response?.data?.error || 'Fehler beim Löschen', 'error');
    }
    setDeleteId(null);
  };

  const csvRef = useRef();
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleCSVImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/admin/questions/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(r.data);
      if (r.data.imported > 0) {
        load();
        toast(`${r.data.imported} Fragen erfolgreich importiert`, 'success');
      }
    } catch (err) {
      setImportResult({ imported: 0, errors: [err.response?.data?.error || 'Import fehlgeschlagen'] });
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const [showCsvHelp, setShowCsvHelp] = useState(false);

  // Lädt die Excel-Vorlage (.xlsx mit Dropdowns) vom Server herunter.
  const downloadTemplate = async () => {
    try {
      const r = await api.get('/admin/questions/template.xlsx', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ilmify-fragen-vorlage.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast('Vorlage konnte nicht geladen werden', 'error');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    const qid = imageTargetId.current;
    if (!file || !qid) return;
    setImageUploading(qid);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const r = await api.post(`/admin/questions/${qid}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setQuestions(prev => prev.map(q => q.id === qid ? { ...q, image_path: r.data.imagePath } : q));
    } catch (err) {
      toast(err.response?.data?.error || 'Bild-Upload fehlgeschlagen', 'error');
    } finally {
      setImageUploading(null);
      e.target.value = '';
    }
  };

  const handleImageDelete = async (qid) => {
    try {
      await api.delete(`/admin/questions/${qid}/image`);
      setQuestions(prev => prev.map(q => q.id === qid ? { ...q, image_path: null } : q));
    } catch (err) {
      toast(err.response?.data?.error || 'Fehler beim Löschen des Bildes', 'error');
    }
  };

  const setF = (key, val) => setFilter(p => ({ ...p, [key]: val }));

  return (
    <div style={{ display: 'flex' }}>
      <AdminSidebar />
      <main className="main-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <div className="page-header" style={{ margin: 0 }}>
            <h1>Fragenverwaltung</h1>
            <p>{total} Fragen ({counts.central} global · {counts.own} eigene)</p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input ref={csvRef} type="file" accept=".csv,.xlsx" style={{ display: 'none' }} onChange={handleCSVImport} />
            <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
            <button className="btn btn-ghost" onClick={downloadTemplate}>
              Vorlage herunterladen
            </button>
            <button className="btn btn-ghost" onClick={() => setShowCsvHelp(v => !v)}>
              Anleitung
            </button>
            {hasFeature('csv_import') && (
              <button className="btn btn-ghost" onClick={() => csvRef.current.click()} disabled={importing}>
                {importing ? 'Wird importiert...' : 'Fragen importieren'}
              </button>
            )}
            <button className="btn btn-primary" onClick={() => setModal('new')}>
              + Neue Frage
            </button>
          </div>
        </div>

        {/* Import result — oben, direkt sichtbar */}
        {importResult && (
          <div style={{ margin: '0 0 16px', padding: '12px 16px', borderRadius: 8, background: importResult.imported > 0 ? '#ECFDF5' : '#FEF3C7', border: `1px solid ${importResult.imported > 0 ? '#6EE7B7' : '#FCD34D'}` }}>
            <strong>{importResult.imported} Fragen importiert</strong> (von {importResult.total || '?'} gesamt)
            {importResult.categoriesCreated > 0 && (
              <div style={{ marginTop: 4, fontSize: 13 }}>{importResult.categoriesCreated} neue Kategorie(n) automatisch erstellt</div>
            )}
            {importResult.errors?.length > 0 && (
              <ul style={{ marginTop: 8, fontSize: 12, color: '#92400E' }}>
                {importResult.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                {importResult.errors.length > 5 && <li>... und {importResult.errors.length - 5} weitere</li>}
              </ul>
            )}
            <button style={{ marginTop: 8, fontSize: 11, cursor: 'pointer', background: 'none', border: 'none', color: 'var(--text3)' }} onClick={() => setImportResult(null)}>Schließen</button>
          </div>
        )}

        {showCsvHelp && (
          <div style={{ margin: '0 0 20px', padding: '16px 18px', borderRadius: 10, background: '#F8FAFC', border: '1px solid #E2E8F0', fontSize: 14, lineHeight: 1.6 }}>
            <strong style={{ display: 'block', marginBottom: 6 }}>CSV vorbereiten — Anleitung</strong>
            <p style={{ margin: '0 0 10px' }}>
              Laden Sie die Vorlage mit "Vorlage herunterladen" herunter, füllen Sie die Zeilen aus und laden Sie
              dieselbe Datei über "CSV Import" wieder hoch. Speichern Sie die Datei als CSV (UTF-8). Die erste Zeile
              (Spaltennamen) darf nicht geändert werden.
            </p>
            <ul style={{ margin: '0 0 10px', paddingLeft: 20 }}>
              <li><b>Frage Deutsch</b> — Pflicht. <b>Frage Albanisch</b> — optional (leer = Deutsch wird verwendet)</li>
              <li><b>Antwort A</b> und <b>Antwort B</b> (Deutsch) — Pflicht bei Multiple-Choice. Albanisch optional.</li>
              <li><b>Antwort C / Antwort D</b> — optional (leer lassen, wenn nicht vorhanden)</li>
              <li><b>Richtige Antwort</b> — <b>A</b>, <b>B</b>, <b>C</b> oder <b>D</b> — Pflicht</li>
              <li><b>Typ</b> — leer = Multiple-Choice; <b>Wahr-Falsch</b> für Wahr/Falsch-Fragen</li>
              <li><b>Kategorie</b> — Name der Kategorie — Pflicht. <b>Existiert sie nicht, wird sie automatisch erstellt.</b></li>
              <li><b>Schwierigkeit</b> — Leicht / Mittel / Schwer (Standard: Mittel)</li>
              <li><b>Quelle</b> — Referenz (optional)</li>
            </ul>
            <p style={{ margin: '0 0 8px' }}>
              <b>Wahr/Falsch:</b> setzen Sie <b>Typ = Wahr-Falsch</b> und <b>Richtige Antwort = A</b> (Wahr) oder <b>B</b> (Falsch).
              Die Antworten dürfen leer bleiben — sie werden automatisch als Wahr/Falsch gefüllt.
            </p>
            <p style={{ margin: '0 0 8px' }}>
              Nur Deutsch ist Pflicht — Albanisch ist optional. Die Excel-Datei (.xlsx) mit den Dropdowns kann direkt
              wieder hochgeladen werden (CSV wird ebenfalls akzeptiert).
            </p>
            {categories.length > 0 && (
              <div style={{ fontSize: 13, color: '#475569' }}>
                <b>Vorhandene Kategorien</b> (können bei "category" verwendet werden):{' '}
                {categories.map((c) => c.name_de).join(', ')}
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          <input
            className="form-input"
            style={{ width: 240 }}
            placeholder="Suchen..."
            value={filter.search}
            onChange={e => setF('search', e.target.value)}
          />
          <select className="form-select" style={{ width: 180 }} value={filter.category} onChange={e => setF('category', e.target.value)}>
            <option value="">Alle Kategorien</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name_de}</option>)}
          </select>
          <select className="form-select" style={{ width: 160 }} value={filter.level} onChange={e => setF('level', e.target.value)}>
            <option value="">Alle Schwierigkeitsgrade</option>
            <option value="easy">Leicht</option>
            <option value="medium">Mittel</option>
            <option value="hard">Schwer</option>
          </select>
          <select className="form-select" style={{ width: 180 }} value={filter.source} onChange={e => setF('source', e.target.value)}>
            <option value="">Alle Fragen</option>
            <option value="global">Globale Fragen</option>
            <option value="own">Fragen der Moschee</option>
          </select>
          {(filter.search || filter.category || filter.level || filter.source) && (
            <button className="btn btn-ghost btn-sm" onClick={() => setFilter({ category: '', level: '', search: '', source: '' })}>
              Filter zurücksetzen
            </button>
          )}
        </div>

        <div className="card">
          {loading ? (
            <div className="spinner-wrap"><div className="spinner" /></div>
          ) : questions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text3)' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>❓</div>
              <p style={{ marginBottom: 20 }}>Keine Fragen gefunden</p>
              <button className="btn btn-primary" onClick={() => setModal('new')}>Erste Frage erstellen</button>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Frage</th>
                    <th>Kategorie</th>
                    <th>Niveau</th>
                    <th>Typ</th>
                    <th>Quelle</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map(q => {
                    const lvl = LEVEL_INFO[q.level] || { cls: 'badge-gray', label: q.level };
                    const isOwn = !q.is_central;
                    return (
                      <tr key={q.id}>
                        <td style={{ maxWidth: 360 }}>
                          <div style={{ fontWeight: 500, fontSize: 14 }}>{q.text_de}</div>
                          {q.text_al && (
                            <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2, fontStyle: 'italic' }}>
                              {q.text_al.length > 80 ? q.text_al.slice(0, 80) + '…' : q.text_al}
                            </div>
                          )}
                          {q.is_central && (
                            <span className="badge badge-blue" style={{ marginTop: 4, fontSize: 10 }}>Zentral</span>
                          )}
                        </td>
                        <td style={{ color: 'var(--text2)', fontSize: 13, whiteSpace: 'nowrap' }}>
                          {q.category_name_de}
                        </td>
                        <td><span className={`badge ${lvl.cls}`}>{lvl.label}</span></td>
                        <td style={{ fontSize: 12, color: 'var(--text3)' }}>
                          {q.type === 'true_false' ? 'W/F' : 'MC'}
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text3)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {q.source_ref || '—'}
                        </td>
                        <td>
                          {hasFeature('photo_questions') && !q.is_central && (
                            <div style={{ marginBottom: 4 }}>
                              {q.image_path ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <img src={`/uploads/${q.image_path}`} alt="" style={{ width: 40, height: 32, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--border)' }} />
                                  <button className="btn btn-sm btn-danger" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => handleImageDelete(q.id)}>✕</button>
                                </div>
                              ) : (
                                <button className="btn btn-sm btn-ghost" style={{ fontSize: 11 }}
                                  disabled={imageUploading === q.id}
                                  onClick={() => { imageTargetId.current = q.id; imageInputRef.current.click(); }}>
                                  {imageUploading === q.id ? '...' : '+ Bild'}
                                </button>
                              )}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: 6 }}>
                            {isOwn ? (
                              <>
                                <button className="btn btn-sm btn-ghost" onClick={() => setModal(q)}>Bearbeiten</button>
                                <button className="btn btn-sm btn-danger" onClick={() => setDeleteId(q.id)}>Löschen</button>
                              </>
                            ) : (
                              <button className="btn btn-sm btn-ghost" onClick={() => setModal({ ...q, _readOnly: true })}>Anzeigen</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>


        {/* Question modal */}
        {modal && (
          <QuestionModal
            question={modal === 'new' ? null : modal}
            onClose={() => setModal(null)}
            onSaved={handleSaved}
          />
        )}

        {/* Delete confirm */}
        {deleteId && (
          <div className="modal-overlay">
            <div className="modal" style={{ maxWidth: 400 }}>
              <h3 className="modal-title">Frage löschen?</h3>
              <p style={{ color: 'var(--text2)', marginBottom: 24 }}>
                Diese Aktion kann nicht rückgängig gemacht werden.
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="btn btn-ghost" onClick={() => setDeleteId(null)}>Abbrechen</button>
                <button className="btn btn-danger" onClick={() => handleDelete(deleteId)}>Löschen</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
